import { useRef, useEffect, useCallback } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useSSE } from './useSSE'
import { queryKeys } from '@/lib/queryKeys'
import {
  updateDagRowsInCache,
  type DagRowCacheUpdate,
} from '@/lib/dagCacheUtils'
import { useDagTableStore } from '@/stores/useDagTableStore'
import { useSseStatusStore } from '@/stores/useSseStatusStore'
import type {
  DagSSERowComputing,
  DagSSECellComputed,
  DagSSEAggregateUpdate,
} from '@/types/dag'

interface DagSSERowUpdateCell {
  column_id: string
  value: unknown
  status?: string
}

// A single edit fans out into dozens of cell:computed / row:update events.
// Writing each one to the React Query cache re-rendered the whole table per
// event; buffer them and write once per short window instead.
const CACHE_FLUSH_DELAY_MS = 32

interface DagSSERowUpdatePayload {
  row_id: string
  cells: Record<string, unknown> | DagSSERowUpdateCell[]
  row_version?: number
  new_version?: number
}

export function useDagSSE(
  planId: string | undefined,
  versionId: string | undefined
) {
  const queryClient = useQueryClient()
  const store = useDagTableStore
  const sseReconnectNonce = useDagTableStore(state => state.sseReconnectNonce)
  const sseEnabled = !!planId && !!versionId

  const pendingUpdatesRef = useRef(new Map<string, DagRowCacheUpdate>())
  const pendingCellClearsRef = useRef<{ rowId: string; colId: string }[]>([])
  const pendingRowClearsRef = useRef<string[]>([])
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const flushPendingUpdates = useCallback(() => {
    if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = null
    const cellClears = pendingCellClearsRef.current
    const rowClears = pendingRowClearsRef.current
    pendingCellClearsRef.current = []
    pendingRowClearsRef.current = []
    store.getState().clearComputingBatch(cellClears, rowClears)

    const pending = pendingUpdatesRef.current
    if (pending.size === 0 || !planId || !versionId) return
    pendingUpdatesRef.current = new Map()
    updateDagRowsInCache(queryClient, planId, versionId, pending)
  }, [queryClient, planId, versionId, store])

  const scheduleFlush = useCallback(() => {
    if (flushTimerRef.current === null) {
      flushTimerRef.current = setTimeout(
        flushPendingUpdates,
        CACHE_FLUSH_DELAY_MS
      )
    }
  }, [flushPendingUpdates])

  const queueRowUpdate = useCallback(
    (rowId: string, cells: Record<string, unknown>, newVersion?: number) => {
      const pending = pendingUpdatesRef.current
      const prev = pending.get(rowId)
      pending.set(rowId, {
        cells: prev ? { ...prev.cells, ...cells } : cells,
        newVersion:
          newVersion !== undefined && prev?.newVersion !== undefined
            ? Math.max(newVersion, prev.newVersion)
            : (newVersion ?? prev?.newVersion),
      })
      scheduleFlush()
    },
    [scheduleFlush]
  )

  // Flush whatever is buffered before the plan/version changes or unmount,
  // so the pending writes land in the cache they belong to.
  useEffect(() => {
    return () => {
      if (flushTimerRef.current !== null) flushPendingUpdates()
    }
  }, [flushPendingUpdates])

  // Reset computing state when version changes
  const prevVersionRef = useRef(versionId)
  useEffect(() => {
    if (prevVersionRef.current !== versionId) {
      store.getState().reset()
      prevVersionRef.current = versionId
    }
  }, [versionId, store])

  const handleMessage = useCallback(
    (event: { type: string; data: Record<string, unknown> }) => {
      const { data } = event

      const payloadTypeCandidates = [
        data.type,
        data.event,
        data.event_name,
        data.eventType,
      ]

      const payloadType = payloadTypeCandidates.find(
        value => typeof value === 'string' && value.length > 0
      ) as string | undefined

      const eventType =
        event.type && event.type !== 'message' ? event.type : payloadType

      const normalizedType = (eventType ?? '')
        .toLowerCase()
        .replace(/[._-]/g, ':')

      const nestedPayload =
        (data.payload as Record<string, unknown> | undefined) ??
        (data.data as Record<string, unknown> | undefined)

      const payload =
        nestedPayload && typeof nestedPayload === 'object'
          ? nestedPayload
          : data

      switch (normalizedType) {
        case 'row:computing': {
          const rowComputing = payload as unknown as DagSSERowComputing
          // Apply buffered clears first so they can't wipe these new flags.
          flushPendingUpdates()
          store
            .getState()
            .setComputing(rowComputing.row_id, rowComputing.columns)
          break
        }

        case 'cell:computed': {
          const cellComputed = payload as unknown as DagSSECellComputed
          pendingCellClearsRef.current.push({
            rowId: cellComputed.row_id,
            colId: cellComputed.column_id,
          })

          // Update the cell value in React Query cache
          queueRowUpdate(cellComputed.row_id, {
            [cellComputed.column_id]: cellComputed.value,
          })
          break
        }

        case 'row:update': {
          const rowUpdate = payload as unknown as DagSSERowUpdatePayload
          const normalizedCells = Array.isArray(rowUpdate.cells)
            ? rowUpdate.cells.reduce<Record<string, unknown>>((acc, cell) => {
                if (cell?.column_id) {
                  acc[cell.column_id] = cell.value
                }
                return acc
              }, {})
            : rowUpdate.cells

          const nextRowVersion = rowUpdate.row_version ?? rowUpdate.new_version
          pendingRowClearsRef.current.push(rowUpdate.row_id)

          // Update entire row in React Query cache
          queueRowUpdate(rowUpdate.row_id, normalizedCells, nextRowVersion)
          break
        }

        case 'aggregate:update': {
          const aggregateUpdate = payload as unknown as DagSSEAggregateUpdate
          store.getState().mergeAggregates(aggregateUpdate.aggregates)
          break
        }
      }
    },
    [store, queueRowUpdate, flushPendingUpdates]
  )

  const handleReconnect = useCallback(() => {
    // The refetch below supersedes anything still buffered.
    if (flushTimerRef.current !== null) clearTimeout(flushTimerRef.current)
    flushTimerRef.current = null
    pendingUpdatesRef.current = new Map()
    pendingCellClearsRef.current = []
    pendingRowClearsRef.current = []
    store.getState().reset()
    queryClient.invalidateQueries({
      queryKey: queryKeys.dagTable.rowsPrefix(planId!, versionId!),
    })
  }, [queryClient, planId, versionId, store])

  const url =
    planId && versionId
      ? `/plans/${planId}/versions/${versionId}/table/events${sseReconnectNonce > 0 ? `?r=${sseReconnectNonce}` : ''}`
      : null

  const streamId = planId && versionId ? `dag-${planId}-${versionId}` : null

  const handleHealthChange = useCallback(
    (healthy: boolean) => {
      if (!streamId) return
      useSseStatusStore.getState().setHealthy(streamId, healthy)
    },
    [streamId]
  )

  useEffect(() => {
    if (!streamId) return
    const unregister = useSseStatusStore.getState().unregister
    return () => {
      unregister(streamId)
    }
  }, [streamId])

  useSSE({
    url,
    streamId,
    onMessage: handleMessage,
    onReconnect: handleReconnect,
    onHealthChange: handleHealthChange,
    enabled: sseEnabled,
    retry: true,
    retryInterval: 3000,
    autoDisconnectOnTerminal: false,
    openWhenHidden: false,
  })
}
